// components/problems/problems-sort-popover.tsx
import {
  ArrowDownUp,
  ArrowDownWideNarrow,
  ArrowUpNarrowWide,
  Check,
  Eye,
  EyeOff,
  RotateCcw,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface SortOptionItem {
  value: string
  label: string
}

const DEFAULT_SORT_OPTIONS: SortOptionItem[] = [
  { value: 'default', label: 'Custom' },
  { value: 'difficulty', label: 'Difficulty' },
  { value: 'questionId', label: 'Question ID' },
  { value: 'status', label: 'Status' },
]

interface ProblemsSortPopoverProps {
  sortBy: string
  sortDir: 'asc' | 'desc'
  onSortChange: (sortBy: string, sortDir: 'asc' | 'desc') => void
  showTags?: boolean
  onToggleTags?: () => void
  sortOptions?: SortOptionItem[]
  /** The value that represents "default/no sort". Defaults to 'default'. */
  defaultValue?: string
  /** Called when the user clicks the Reset button. */
  onReset?: () => void
  /** When true, the trigger always appears active (blue) regardless of current sort. */
  alwaysActive?: boolean
}

export function ProblemsSortPopover({
  sortBy,
  sortDir,
  onSortChange,
  showTags,
  onToggleTags,
  sortOptions = DEFAULT_SORT_OPTIONS,
  defaultValue = 'default',
  onReset,
  alwaysActive = false,
}: ProblemsSortPopoverProps) {
  const [open, setOpen] = useState(false)
  const tourHeld = useRef(false)

  useEffect(() => {
    const handleOpen = () => {
      tourHeld.current = true
      setOpen(true)
    }
    const handleClose = () => {
      tourHeld.current = false
      setOpen(false)
    }
    document.addEventListener('tour:open-sort', handleOpen)
    document.addEventListener('tour:close-sort', handleClose)
    return () => {
      document.removeEventListener('tour:open-sort', handleOpen)
      document.removeEventListener('tour:close-sort', handleClose)
    }
  }, [])

  const handleOpenChange = useCallback((next: boolean) => {
    if (!next && tourHeld.current) return
    setOpen(next)
  }, [])

  const isActive = alwaysActive || sortBy !== defaultValue
  const activeLabel = sortOptions.find((o) => o.value === sortBy)?.label

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition-colors',
            isActive
              ? 'bg-muted font-medium'
              : 'text-muted-foreground hover:text-foreground',
          )}
          style={isActive ? { color: '#448EF7' } : undefined}
        >
          {isActive ? (
            sortDir === 'asc' ? (
              <ArrowUpNarrowWide className="h-4 w-4" />
            ) : (
              <ArrowDownWideNarrow className="h-4 w-4" />
            )
          ) : (
            <ArrowDownUp className="h-4 w-4" />
          )}
          {isActive && <span>{activeLabel}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="problems-table-filter w-48 p-1">
        {sortOptions.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => {
              if (sortBy === opt.value && opt.value !== defaultValue) {
                onSortChange(opt.value, sortDir === 'asc' ? 'desc' : 'asc')
              } else {
                onSortChange(opt.value, 'asc')
              }
            }}
            className={cn(
              'flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm',
              sortBy === opt.value
                ? 'text-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {opt.label}
            {sortBy === opt.value &&
              opt.value !== defaultValue &&
              (sortDir === 'asc' ? (
                <ArrowUpNarrowWide className="h-4 w-4" />
              ) : (
                <ArrowDownWideNarrow className="h-4 w-4" />
              ))}
            {sortBy === opt.value && opt.value === defaultValue && (
              <Check className="h-4 w-4" />
            )}
          </button>
        ))}
        {onToggleTags !== undefined && (
          <>
            <div className="border-border my-1 border-t" />
            <button
              type="button"
              onClick={onToggleTags}
              className="text-muted-foreground hover:text-foreground flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm"
            >
              Tags
              {showTags ? (
                <Eye className="h-4 w-4" />
              ) : (
                <EyeOff className="h-4 w-4" />
              )}
            </button>
          </>
        )}
        {onReset && (
          <>
            <div className="border-border my-1 border-t" />
            <button
              type="button"
              onClick={onReset}
              className="text-muted-foreground hover:text-foreground inline-flex w-full items-center gap-1 rounded px-3 py-2 text-sm"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset
            </button>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}
